import { useEffect } from "react";
import {
  SCROLL_HIGHLIGHT_QUERY_STRING,
  SCROLL_QUERY_STRING,
} from "../constants";
import { removeQueryParam } from "../helpers";
import { useNavigation } from "./useNavigation";

export const scrollToElement = ({
  elementId,
  highlight = false,
  highlightParams = {},
  scrollParams = {},
}) => {
  const scrollDelay = scrollParams.delay || 1000;
  let highlightTimeoutId = null;
  const scrollTimeoutId = setTimeout(() => {
    const element = document.getElementById(elementId);
    element?.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    });
    if (highlight)
      highlightTimeoutId = highlightElement(element, highlightParams);
  }, scrollDelay);
  return [scrollTimeoutId, highlightTimeoutId];
};

export const highlightElement = (element, highlightParams = {}) => {
  const color = highlightParams.color || theme.palette.yellow.primary;
  const opacity = highlightParams.opacity || 1;
  const visibility = highlightParams.visibility || "visible";
  const removeDelay = highlightParams.removeDelay || 2000;
  const duration = highlightParams.duration || removeDelay / 1000;
  // Apply inline styles to highlight the element
  element.style.cssText = `visibility: ${visibility}; background-color: ${color}; opacity: ${opacity}; transition: background-color ${duration}s ease-in-out, opacity ${duration}s ease-in-out;`;

  // Remove the highlight after a delay
  const highlightTimeoutId = setTimeout(() => {
    element.style.cssText = "";
  }, removeDelay);
  return highlightTimeoutId;
};

/**
 * Custom hook to handle scrolling and highlighting elements based on query parameters or manual triggers.
 * @param {Object} params - Parameters for scrolling and highlighting.
 * @param {Object} params.highlightParams - Parameters for highlighting the element.
 * @param {Object} params.scrollParams - Parameters for scrolling the element.
 * To use this with query parameters, ensure that the URL contains:
 * - `scrollElementId` for scrolling to an element.
 * - `scrollHighlightElementId` for scrolling to an element and highlighting it.
 * Example:
 * `?scrollElementId=elementId`
 * or
 * `scrollHighlightElementId=highlightedElementId`
 *
 * or for manual triggers:
 * `scrollAndHighlightElement({ elementId: 'elementId', highlight: true, highlightParams: { color: '#FFE250', opacity: 0.5 }, scrollParams: { delay: 500 } })`
 */
export const useScrollAndHighlight = ({ highlightParams, scrollParams }) => {
  const { searchParams } = useNavigation();

  const scrollAndHighlightElement = ({
    elementId,
    highlight = false,
    highlightParams,
    scrollParams,
  }) => {
    scrollToElement({ elementId, highlight, highlightParams, scrollParams });
  };

  useEffect(() => {
    const highlight = Boolean(searchParams[SCROLL_HIGHLIGHT_QUERY_STRING]);
    const scrollToElementId =
      searchParams[SCROLL_HIGHLIGHT_QUERY_STRING] ||
      searchParams[SCROLL_QUERY_STRING];
    let timeoutIds = [];
    if (scrollToElementId) {
      timeoutIds = scrollToElement({
        elementId: scrollToElementId,
        highlight,
        highlightParams,
        scrollParams,
      });
      removeQueryParam(SCROLL_QUERY_STRING); //Remove scroll query param after scrolling
      removeQueryParam(SCROLL_HIGHLIGHT_QUERY_STRING); //Remove highlight query param after
    }
    return () => {
      // Clear any timeouts to prevent memory leaks
      timeoutIds?.forEach((timeoutId) => timeoutId && clearTimeout(timeoutId));
    };
  }, []);

  return {
    scrollAndHighlightElement,
  };
};
